// src/components/wells/WellIntelCommandStrip.js
// FlowSense MOW+ — fleet-level Well & Drainage Intelligence strip for the Live Readings page.
import React, { memo } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Badge, Icon, SimpleGrid, Tooltip, Button, IconButton, Skeleton,
  Wrap, WrapItem, Alert, AlertIcon, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAutoAwesome, MdWaterDrop, MdSensors, MdAssignmentLate, MdBuild, MdWaves, MdRefresh, MdWarning, MdBolt,
} from 'react-icons/md';
import { AI_GRADIENT, tierMeta, formatAgo } from './wellIntelUi';

const Kpi = ({ icon, label, value, sub, color, onClick, tooltip }) => {
  const bg = useColorModeValue('whiteAlpha.800', 'whiteAlpha.50');
  const border = useColorModeValue('purple.100', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Tooltip label={tooltip} hasArrow isDisabled={!tooltip} openDelay={250}>
      <Box
        as={onClick ? 'button' : 'div'} onClick={onClick} textAlign="left" p={3} bg={bg} borderWidth="1px" borderColor={border}
        borderRadius="xl" backdropFilter="blur(8px)" transition="transform .15s ease, box-shadow .15s ease"
        _hover={onClick ? { transform: 'translateY(-1px)', boxShadow: 'md' } : undefined}
      >
        <HStack justify="space-between" mb={1}>
          <Text fontSize="10px" fontWeight="800" letterSpacing="0.06em" textTransform="uppercase" color={muted}>{label}</Text>
          <Icon as={icon} boxSize={4} color={color || muted} />
        </HStack>
        <Text fontSize="xl" fontWeight="900" color={color} lineHeight="1.1">{value}</Text>
        {sub && <Text fontSize="10px" color={muted} mt={0.5} noOfLines={1}>{sub}</Text>}
      </Box>
    </Tooltip>
  );
};

function WellIntelCommandStrip({ summary, isLoading, error, generatedAt, liveEvents = [], isConnected, onSelectWell, onOpenTracker, onRefresh }) {
  const shell = useColorModeValue('linear(to-br, white, purple.50)', 'linear(to-br, gray.800, rgba(68, 51, 122, 0.35))');
  const border = useColorModeValue('purple.200', 'purple.800');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const narrBg = useColorModeValue('purple.50', 'rgba(159, 122, 234, 0.12)');

  if (isLoading && !summary) {
    return <Skeleton h="150px" borderRadius="2xl" mb={6} />;
  }
  const s = summary || {};
  const tiers = s.tiers || {};
  const insp = s.inspections || {};
  const needInspection = (insp.overdue || 0) + (insp.never_inspected || 0);
  const lastLive = liveEvents[0];

  return (
    <Box position="relative" mb={6} p={{ base: 4, md: 5 }} borderRadius="2xl" borderWidth="1.5px" borderColor={border} bgGradient={shell} boxShadow="lg" overflow="hidden">
      <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient={AI_GRADIENT} />
      <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={2} mb={3}>
        <HStack spacing={3}>
          <Box p={2} borderRadius="xl" bgGradient={AI_GRADIENT} color="white" boxShadow="md"><Icon as={MdAutoAwesome} boxSize={5} /></Box>
          <VStack align="start" spacing={0}>
            <Text fontWeight="900" fontSize="lg">Well & Drainage Intelligence</Text>
            <HStack spacing={2} fontSize="xs" color={muted}>
              <Text>{s.total ?? 0} wells in the register{s.kinds ? ` (${s.kinds.drainage} drainage)` : ''}</Text>
              <Text>·</Text>
              <Text>{s.model_version || 'well-intel'}</Text>
              {generatedAt && <><Text>·</Text><Text>updated {formatAgo(generatedAt)}</Text></>}
            </HStack>
          </VStack>
        </HStack>
        <HStack spacing={2}>
          <Badge colorScheme={isConnected ? 'green' : 'gray'} variant="subtle" borderRadius="full" px={2}>
            {isConnected ? 'LIVE' : 'OFFLINE'}
          </Badge>
          {lastLive && (
            <Tooltip label={`Last live update: ${lastLive.name || lastLive.wellId}`} hasArrow>
              <Badge colorScheme="purple" variant="outline" borderRadius="full" px={2} display="flex" alignItems="center" gap={1}>
                <Icon as={MdBolt} boxSize={3} />{formatAgo(lastLive.at)}
              </Badge>
            </Tooltip>
          )}
          {onRefresh && <IconButton size="sm" variant="ghost" icon={<MdRefresh />} onClick={onRefresh} isLoading={isLoading} aria-label="Recompute well intelligence" />}
        </HStack>
      </Flex>

      {error && (
        <Alert status="warning" borderRadius="lg" mb={3} py={2} fontSize="sm"><AlertIcon />{error}</Alert>
      )}

      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
        <Kpi icon={MdWarning} label="At risk" value={s.at_risk ?? 0}
          sub={`${tiers.critical || 0} critical · ${tiers.high || 0} high · ${tiers.watch || 0} watch`}
          color={(tiers.critical || 0) > 0 ? 'red.500' : (s.at_risk || 0) > 0 ? 'orange.400' : 'green.500'}
          tooltip="Explainable risk score ≥ 45 (ML flood probability, level, trend, drainage, inspection, alerts)" />
        <Kpi icon={MdSensors} label="Monitored" value={`${s.monitored ?? 0}/${s.total ?? 0}`}
          sub={`${s.online ?? 0} online · ${s.unmonitored ?? 0} without sensor`} color="blue.500"
          onClick={onOpenTracker ? () => onOpenTracker('monitoring') : undefined} />
        <Kpi icon={MdAssignmentLate} label="Inspections" value={needInspection}
          sub={`${insp.overdue || 0} overdue · ${insp.never_inspected || 0} never · ${insp.due_soon || 0} due soon`}
          color={needInspection ? 'orange.400' : 'green.500'} onClick={onOpenTracker ? () => onOpenTracker('inspections') : undefined} />
        <Kpi icon={MdWaterDrop} label="Silt cleaning" value={insp.cleaning_overdue || 0} sub="past the cleaning cycle"
          color={insp.cleaning_overdue ? 'orange.400' : undefined} onClick={onOpenTracker ? () => onOpenTracker('inspections') : undefined} />
        <Kpi icon={MdBuild} label="Open work orders" value={s.open_work_orders ?? 0} sub={`${s.active_alerts ?? 0} active alerts`}
          color="purple.500" onClick={onOpenTracker ? () => onOpenTracker('work_orders') : undefined} />
        <Kpi icon={MdWaves} label="Drainage mapped" value={s.drainage?.coverage_pct != null ? `${s.drainage.coverage_pct}%` : '—'}
          sub={s.linked_drainage ? `${s.linked_drainage.catch_basins} catch basins · ${s.linked_drainage.drainage_wells} drainage wells · ${s.linked_drainage.inspection_overdue + s.linked_drainage.never_inspected} need inspection` : s.drainage ? `${s.drainage.with_drainage}/${s.drainage.located_wells} located wells · ${s.drainage.radius_m} m` : null}
          color="cyan.500" onClick={onOpenTracker ? () => onOpenTracker('drainage') : undefined}
          tooltip={s.drainage ? `Share of located wells with a catch basin / drainage asset within ${s.drainage.radius_m} m (${s.drainage.with_drainage}/${s.drainage.located_wells})${s.linked_drainage?.impaired ? ` · ${s.linked_drainage.impaired} impaired` : ''}` : 'Share of located wells with a mapped drainage asset nearby'} />
      </SimpleGrid>

      <Flex mt={3} gap={3} direction={{ base: 'column', lg: 'row' }} align={{ lg: 'center' }}>
        <HStack align="start" spacing={2} p={2.5} bg={narrBg} borderRadius="lg" flex="1" minW={0}>
          <Icon as={MdAutoAwesome} color="purple.400" mt="2px" />
          <Text fontSize="sm" fontWeight="600">{s.narrative || 'Waiting for the first evaluation…'}</Text>
        </HStack>
        {Array.isArray(s.top_risk) && s.top_risk.length > 0 && (
          <Wrap spacing={1.5}>
            {s.top_risk.slice(0, 4).map((w) => {
              const tm = tierMeta(w.tier);
              return (
                <WrapItem key={String(w.id)}>
                  <Tooltip label={w.summary} hasArrow>
                    <Button size="xs" borderRadius="full" variant="outline" colorScheme={tm.scheme} onClick={() => onSelectWell?.(w.id)}>
                      {w.name || `Well ${w.id}`} · {w.score}
                    </Button>
                  </Tooltip>
                </WrapItem>
              );
            })}
          </Wrap>
        )}
      </Flex>
    </Box>
  );
}

export default memo(WellIntelCommandStrip);
